import { createContext, useContext } from 'react';

/**
 * DARK-ONLY THEME (owner ruling 2026-08-01, platform restyle PT-0 decisions —
 * docs/DESIGN_PLATFORM_THEME_AUDIT.md).
 *
 * Light mode was removed with the prototype design-language adoption: the
 * approved look (glows, hairlines on near-black #0A0E13) does not translate to
 * a light canvas, and a parallel light token set doubled the design and
 * verification surface of every rollout PR. The `[data-theme="light"]` token
 * block was deleted from index.css and the TopBar toggle removed in the same
 * change.
 *
 * The provider + useTheme() API SHAPE is retained so existing consumers keep
 * compiling: `theme` is always 'dark'; toggleTheme/setTheme are no-ops. If
 * light mode ever returns, restore from git history (this file, the index.css
 * block, and the TopBar button).
 */

type Theme = 'dark';

interface ThemeContextValue {
  theme: Theme;
  /** No-op — dark-only. Retained for API compatibility. */
  toggleTheme: () => void;
  /** No-op — dark-only. Retained for API compatibility. */
  setTheme: (theme: Theme) => void;
}

// Exported so ThemeProvider.tsx (react-refresh/only-export-components split
// — see that file's comment) can construct the Provider.
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const DARK_ONLY_VALUE: ThemeContextValue = {
  theme: 'dark',
  toggleTheme: () => {},
  setTheme: () => {},
};

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
